import { ERROR_MESSAGES, apiErrorSchema } from '@apovisio/contracts';
import type { ErrorCode } from '@apovisio/contracts';
import type { z } from 'zod';

/** Erreur d'API normalisée : le code stable pilote l'affichage, jamais le texte. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly errorCode: ErrorCode,
    message: string,
    readonly fields?: Record<string, string>,
    /** Aucune réponse du serveur (réseau coupé, serveur injoignable). */
    readonly network = false,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export const NETWORK_ERROR_MESSAGE = 'Connexion au serveur impossible. Vérifiez votre réseau.';

interface ApiFetchOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  schema?: z.ZodType<T>;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, signal } = options;
  const headers: Record<string, string> = { accept: 'application/json' };
  // En-tête personnalisé : une requête inter-origines ne peut pas l'ajouter sans preflight CORS.
  if (method !== 'GET') headers['x-apovisio-request'] = '1';
  if (body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiClientError(0, 'INTERNAL_ERROR', NETWORK_ERROR_MESSAGE, undefined, true);
  }

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) {
      const { statusCode, errorCode, message, fields } = parsed.data;
      throw new ApiClientError(statusCode, errorCode, message, fields);
    }
    const fallback: ErrorCode = response.status === 429 ? 'RATE_LIMITED' : 'INTERNAL_ERROR';
    throw new ApiClientError(response.status, fallback, ERROR_MESSAGES[fallback]);
  }

  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json();
  return schema ? schema.parse(data) : (data as T);
}
